import {
  type KeyboardEvent,
  type MouseEvent,
  memo,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import clsx from 'clsx';

import { AppScrollArea } from '@/components/ui/AppScrollArea';
import { useGeoMarkupStore } from '@/store';
import type { GeoMarkupStore } from '@/store';
import {
  getAllArtifacts,
  useArtifactSwitch,
  type ArtifactDefinition,
  type ArtifactId,
} from '@/artifacts';

import styles from './ArtifactBar.module.css';

const ARTIFACT_ICON_SIZE = 16;

interface ArtifactBarProps {
  readonly activeArtifact: ArtifactId;
}

type IndicatorKey = `${ArtifactId}:${'filters' | 'visible'}`;
type ArtifactIndicatorsMap = Readonly<Partial<Record<IndicatorKey, boolean>>>;

/**
 * Build a flat indicators map from the registry. Keys use the pattern
 * `"<id>:filters"` / `"<id>:visible"` so every value is a boolean.
 * `useShallow` then compares booleans by value (not object identity),
 * preventing re-renders when unrelated store fields change.
 */
function selectArtifactIndicators(
  state: GeoMarkupStore
): ArtifactIndicatorsMap {
  const result = {} as Record<IndicatorKey, boolean>;
  for (const artifact of getAllArtifacts()) {
    const id = artifact.id as ArtifactId;
    result[`${id}:filters`] = artifact.selectors.hasActiveFilters(state);
    result[`${id}:visible`] = artifact.selectors.isLayerVisible(state);
  }
  return result;
}

export const ArtifactBar = memo(function ArtifactBar({
  activeArtifact,
}: ArtifactBarProps) {
  const { t } = useTranslation();
  const { navigateToArtifact, buildHref } = useArtifactSwitch();
  const linkRefs = useRef<(HTMLAnchorElement | null)[]>([]);

  // Single registry-driven subscription. `useShallow` compares the outer
  // object by member equality so the component only re-renders when one
  // of the indicator flags actually flips.
  const indicators = useGeoMarkupStore(useShallow(selectArtifactIndicators));

  const artifacts = useMemo(() => getAllArtifacts(), []);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>, artifact: ArtifactDefinition) => {
      event.preventDefault();
      navigateToArtifact(artifact);
    },
    [navigateToArtifact]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLAnchorElement>) => {
      const currentIndex = linkRefs.current.findIndex(
        (ref) => ref === event.currentTarget
      );
      if (currentIndex === -1) return;

      const artifactCount = artifacts.length;
      if (artifactCount === 0) return;

      let nextIndex = -1;

      if (event.key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % artifactCount;
      } else if (event.key === 'ArrowLeft') {
        nextIndex = (currentIndex - 1 + artifactCount) % artifactCount;
      }

      if (nextIndex !== -1) {
        event.preventDefault();
        linkRefs.current[nextIndex]?.focus();
      }
    },
    [artifacts.length]
  );

  return (
    <AppScrollArea className={styles.barScroll}>
      <nav
        className={styles.bar}
        role="tablist"
        aria-label={t('appShell.sidebar.navigation')}
      >
        {artifacts.map((artifact, index) => {
          const isActive = activeArtifact === artifact.id;
          const IconComponent = artifact.icon;
          const fullPath = buildHref(artifact);

          const id = artifact.id as ArtifactId;
          const hasActiveFilters = indicators[`${id}:filters`] ?? false;
          const isLayerVisible = indicators[`${id}:visible`] ?? true;

          return (
            <a
              key={artifact.id}
              ref={(element) => {
                linkRefs.current[index] = element;
              }}
              href={fullPath}
              role="tab"
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              className={clsx(styles.item, isActive && styles.itemActive)}
              onClick={(event) => handleClick(event, artifact)}
              onKeyDown={handleKeyDown}
            >
              <span className={styles.iconWrapper}>
                <IconComponent size={ARTIFACT_ICON_SIZE} />
                {hasActiveFilters && <span className={styles.filterDot} />}
                {!isLayerVisible && <span className={styles.hiddenLayerDot} />}
              </span>
              {t(artifact.labelKey)}
            </a>
          );
        })}
      </nav>
    </AppScrollArea>
  );
});
